import {useCallback, useSyncExternalStore} from 'react'

export type OnboardingAnchorId = 'chat' | 'owner' | 'environment'

export function isVisibleOnboardingAnchor(
  button: HTMLButtonElement | HTMLAnchorElement,
  scope?: Element | null,
  {inViewport = false}: {inViewport?: boolean} = {},
) {
  if (!button.isConnected || (button instanceof HTMLButtonElement && button.disabled)) return false
  if (button.checkVisibility ? !button.checkVisibility() : button.getClientRects().length === 0) return false
  const rect = button.getBoundingClientRect()
  const {clientWidth, clientHeight} = button.ownerDocument.documentElement
  const bounds = scope?.getBoundingClientRect()
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    (!inViewport || (rect.right > 0 && rect.left < clientWidth && rect.bottom > 0 && rect.top < clientHeight)) &&
    (!bounds ||
      (rect.right > bounds.left && rect.left < bounds.right && rect.bottom > bounds.top && rect.top < bounds.bottom))
  )
}

export function resolveOnboardingAnchor(id: OnboardingAnchorId, scope: Element): HTMLButtonElement | null {
  const wrapper = scope.querySelector(`[data-copilot-onboarding-anchor="${id}"]`)
  for (const button of wrapper?.querySelectorAll('button') ?? []) {
    if (button.closest('[data-copilot-onboarding-popover]')) continue
    if (button instanceof HTMLButtonElement && isVisibleOnboardingAnchor(button, scope)) return button
  }
  return null
}

export function useOnboardingAnchor(id: OnboardingAnchorId, scope: Element | null): HTMLButtonElement | null {
  const getSnapshot = useCallback(() => (scope ? resolveOnboardingAnchor(id, scope) : null), [id, scope])
  const subscribe = useCallback(
    (update: () => void) => {
      if (!scope) return () => {}
      const observer = new MutationObserver(update)
      observer.observe(scope, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'hidden', 'disabled'],
      })
      const resizeObserver = new ResizeObserver(update)
      resizeObserver.observe(scope)
      return () => {
        observer.disconnect()
        resizeObserver.disconnect()
      }
    },
    [scope],
  )
  return useSyncExternalStore(subscribe, getSnapshot, () => null)
}
